// Menu de anexos (0.70, texto 09 C1/C2): "Fotos" (primeiro), "Tirar foto" e "Arquivos". Componente único:
// Estudos usa primeiro; as outras abas herdam depois (useAnexar + {anexar.tela}).
//
// Por que é feito assim (C2, iPhone): abrir uma janela do sistema (galeria, arquivos, câmera) enquanto outra
// janela do app ainda está fechando, ou abrir uma janela por cima de outra janela, deixava o iPhone e o app
// discordando sobre o que estava na tela: a folha da matéria sumia, a imagem não entrava e as matérias
// paravam de abrir. Aqui:
// - emLinha: o menu e a câmera aparecem POR CIMA da folha aberta, sem janela nova (o {tela} precisa ficar
//   direto dentro da folha, cobrindo tudo). O seletor do sistema abre logo depois de o menu sumir.
// - sem emLinha: o menu é uma janela leve; ela só aparece depois de a janela anterior terminar de fechar,
//   e o seletor do sistema só abre depois de o menu terminar de fechar (onDismiss no iPhone).
// - A câmera do app (app antigo, sem câmera do sistema) aparece no lugar do menu, sem abrir outra janela.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Modal, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { Anexo } from '../store/types';
import { escolherAnexo, escolhendo, temCameraSistema, temGaleria, type AnexoEscolhido, type OrigemAnexo, type TipoAnexo } from '../lib/anexar';
import { CameraTela, permitirCamera } from './CameraFoto';
import { C } from './theme';
import { t } from '../lib/i18n';

type Pedido = { tipo: TipoAnexo; titulo?: string; varios?: boolean; frente?: boolean; dica?: string; semNuvem?: boolean; res: (r: AnexoEscolhido | null) => void };
type Fase = 'menu' | 'semGaleria' | 'camera' | null;

/** Abre o menu Fotos / Tirar foto / Arquivos e devolve o que foi escolhido (null = cancelou).
 *  Renderize {tela} uma vez no componente. emLinha: {tela} está direto dentro da folha (cobre a folha toda).
 *  semNuvem: não guarda no Drive/OneDrive (itens protegidos pelo cofre).
 *  anexar({ origem }): sem o menu, direto em Fotos, Tirar foto ou Arquivos (botões já separados na tela, como nos
 *  anexos das fichas). Fotos sem a galeria neste app mostra o mesmo aviso do menu. varios: várias fotos de uma vez.
 *  frente/dica: câmera da frente e texto no alto da câmera do próprio app (foto do contato).
 *  semNuvem em anexar: vale só para este pedido (no lugar do semNuvem do hook). */
export function useAnexar(o: { cor: string; emLinha?: boolean; semNuvem?: boolean }) {
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [fase, setFase] = useState<Fase>(null);
  const atual = useRef<Pedido | null>(null);
  const depois = useRef<(() => void) | null>(null);      // o que fazer quando o menu terminar de fechar
  const fotoDoApp = useRef<((a: Anexo | null) => void) | null>(null);
  const montado = useRef(true);
  useEffect(() => () => { montado.current = false; atual.current?.res(null); atual.current = null; }, []);

  const terminar = useCallback((r: AnexoEscolhido | null) => {
    const p = atual.current; atual.current = null;
    if (montado.current) setPedido(null);
    p?.res(r);
  }, []);

  /** Fecha o menu e, só quando ele sumiu de verdade, roda fn (abre o seletor do sistema). */
  const fecharEDepois = useCallback((fn: () => void) => {
    let rodou = false;
    const uma = () => { if (rodou) return; rodou = true; depois.current = null; fn(); };
    setFase(null);
    if (o.emLinha || Platform.OS === 'web') { setTimeout(uma, 60); return; }
    depois.current = uma;                                       // iPhone: onDismiss do menu
    setTimeout(uma, Platform.OS === 'ios' ? 900 : 300);         // garantia (Android não tem onDismiss)
  }, [o.emLinha]);

  const anexar = useCallback((op: { tipo?: TipoAnexo; titulo?: string; origem?: OrigemAnexo; varios?: boolean; frente?: boolean; dica?: string; semNuvem?: boolean } = {}) => new Promise<AnexoEscolhido | null>(res => {
    if (atual.current || escolhendo()) { res(null); return; }   // um por vez
    const p: Pedido = { tipo: op.tipo ?? 'tudo', titulo: op.titulo, varios: op.varios, frente: op.frente, dica: op.dica, semNuvem: op.semNuvem, res };
    atual.current = p; setPedido(p);
    if (op.origem) { diretoRef.current(op.origem); return; }
    // sem emLinha, no iPhone: espera a janela de quem chamou (menu ⋮, "Inserir"...) terminar de fechar
    setTimeout(() => { if (atual.current === p && montado.current) setFase('menu'); }, !o.emLinha && Platform.OS === 'ios' ? 350 : 0);
  }), [o.emLinha]);

  const erro = (e: any) => Alert.alert(t('Não foi possível'), String(e?.message ?? e));
  const rodar = (origem: OrigemAnexo, cameraDoApp?: () => Promise<Anexo | null>) =>
    escolherAnexo({ tipo: atual.current?.tipo, origem, semNuvem: atual.current?.semNuvem ?? o.semNuvem, cameraDoApp, varios: origem !== 'camera' && !!atual.current?.varios, frente: atual.current?.frente })
      .then(terminar)   // r.aviso (Drive/OneDrive falhou) fica para quem chamou mostrar
      .catch(e => { terminar(null); erro(e); });

  /** Sem o menu (anexar({ origem })): só aparece janela do app para o aviso de Fotos ou para a câmera do app. */
  async function direto(origem: OrigemAnexo) {
    // sem emLinha, no iPhone: a janela do app (aviso ou câmera) espera a janela de quem chamou terminar de fechar
    const p = atual.current;
    const mostrar = (f: Fase) => setTimeout(() => { if (atual.current === p && montado.current) setFase(f); }, !o.emLinha && Platform.OS === 'ios' ? 350 : 0);
    if (origem === 'fotos' && !temGaleria() && Platform.OS !== 'web') { mostrar('semGaleria'); return; }
    if (origem === 'camera' && !temCameraSistema()) {
      if (!(await permitirCamera())) { terminar(null); return; }
      mostrar('camera');
      rodar('camera', () => new Promise<Anexo | null>(r => { fotoDoApp.current = r; }));
      return;
    }
    rodar(origem);
  }

  const diretoRef = useRef(direto); diretoRef.current = direto;

  async function escolher(origem: OrigemAnexo) {
    // no navegador não há "app novo": Fotos abre o seletor de imagens do navegador
    if (origem === 'fotos' && !temGaleria() && Platform.OS !== 'web') { setFase('semGaleria'); return; }
    if (origem === 'camera' && !temCameraSistema()) {
      // câmera do próprio app, no lugar do menu (sem abrir outra janela)
      if (!(await permitirCamera())) { fecharEDepois(() => terminar(null)); return; }
      setFase('camera');
      rodar('camera', () => new Promise<Anexo | null>(r => { fotoDoApp.current = r; }));
      return;
    }
    fecharEDepois(() => rodar(origem));
  }
  const cancelar = () => {
    if (fase === 'camera') { fimDaCamera(null); return; }
    fecharEDepois(() => terminar(null));
  };
  const fimDaCamera = (a: Anexo | null) => {
    const r = fotoDoApp.current; fotoDoApp.current = null;
    fecharEDepois(() => r?.(a));
  };

  const conteudo = fase && pedido ? (
    fase === 'camera'
      ? <View style={[StyleSheet.absoluteFill, { backgroundColor: '#000' }]}><CameraTela onFim={fimDaCamera} frente={pedido.frente} dica={pedido.dica ?? t('Enquadre e toque no botão')} /></View>
      : <MenuAnexo fase={fase} titulo={pedido.titulo} cor={o.cor} onEscolher={escolher} onCancelar={cancelar} />
  ) : null;

  const tela = o.emLinha
    ? (conteudo ? <View style={[StyleSheet.absoluteFill, { zIndex: 1000, elevation: 1000 }]}>{conteudo}</View> : null)
    : (
      <Modal visible={!!fase && !!pedido} transparent animationType="fade" onRequestClose={cancelar}
        onDismiss={() => { const f = depois.current; if (f) setTimeout(f, 50); }}>
        {conteudo}
      </Modal>
    );
  return { anexar, tela, aberto: !!pedido };
}

/** O menu em si (folha pequena que sobe de baixo). */
function MenuAnexo({ fase, titulo, cor, onEscolher, onCancelar }: {
  fase: 'menu' | 'semGaleria'; titulo?: string; cor: string; onEscolher: (o: OrigemAnexo) => void; onCancelar: () => void;
}) {
  const ins = useSafeAreaInsets();
  const camera = Platform.OS !== 'web';
  const item = (rotulo: string, f: () => void, destaque = false) => (
    <Pressable key={rotulo} onPress={f} accessibilityRole="button"
      style={({ pressed }) => ({ paddingVertical: 14, paddingHorizontal: 8, borderRadius: 10, backgroundColor: pressed ? C.card2 : 'transparent' })}>
      <Text style={{ fontSize: 16, color: destaque ? cor : C.text, fontWeight: destaque ? '700' : '400' }}>{rotulo}</Text>
    </Pressable>
  );
  return (
    <Pressable onPress={onCancelar} style={{ flex: 1, backgroundColor: C.overlay, justifyContent: 'flex-end' }}>
      <Pressable style={{ backgroundColor: C.card, borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: 16, paddingBottom: ins.bottom + 16, gap: 2 }}>
        {fase === 'menu' ? (
          <>
            <Text style={{ color: C.text, fontWeight: '700', fontSize: 16, marginBottom: 8 }}>{titulo ?? t('Adicionar')}</Text>
            {item('🖼️ ' + t('Fotos'), () => onEscolher('fotos'))}
            {camera && item('📷 ' + t('Tirar foto'), () => onEscolher('camera'))}
            {item('📎 ' + t('Arquivos'), () => onEscolher('arquivos'))}
          </>
        ) : (
          <>
            <Text style={{ color: C.text, fontWeight: '700', fontSize: 16, marginBottom: 6 }}>🖼️ {t('Fotos')}</Text>
            <Text style={{ color: C.sub, fontSize: 14, marginBottom: 8 }}>{t('A galeria chega na próxima atualização do app.')}</Text>
            {item('📎 ' + t('Arquivos'), () => onEscolher('arquivos'), true)}
            {camera && item('📷 ' + t('Tirar foto'), () => onEscolher('camera'))}
          </>
        )}
        {item(t('Cancelar'), onCancelar)}
      </Pressable>
    </Pressable>
  );
}
